import { useCallback, useContext } from 'react';
import { ConfirmationModalContext } from '../util/confirmationModal/ConfirmationModalProvider';

// the label only decorates the buttons - if building it fails (e.g. missing data), fall back to the generic buttons
// rather than breaking the action itself
const resolveConfirmationButtonText = (confirmationButtonText, callbackParams) => {
  if (typeof confirmationButtonText !== 'function') return confirmationButtonText;
  try {
    return confirmationButtonText(...callbackParams);
  } catch (error) {
    console.error('useModalConfirm: could not build confirmationButtonText', error);
    return undefined;
  }
};

/**
 * Wraps an action so calling it opens the confirmation modal first; the action runs only once the user confirms.
 *
 *   const deleteNote = useModalConfirm({
 *     type: 'delete',
 *     confirmationButtonText: (note) => note.title, // a string, or a function of the call's arguments
 *     onConfirm: (note) => notesService.delete(note.id),
 *   })
 *   <MarketDeleteButton onClick={() => deleteNote(note)} />
 *
 * Any other confirm() options (body, title, size...) are passed through. If onConfirm throws, onError / onFinally run,
 * the error is rethrown and the modal shows it and stays open. Must be used inside ConfirmationModalProvider.
 */
export function useModalConfirm(modalConfirm) {
  let onFinally = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let onError = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const setConfirmationModalRequest = useContext(ConfirmationModalContext);
  return useCallback(function () {
    for (var _len = arguments.length, callbackParams = new Array(_len), _key = 0; _key < _len; _key++) {
      callbackParams[_key] = arguments[_key];
    }
    if (!setConfirmationModalRequest) {
      throw new Error('useModalConfirm must be used inside ConfirmationModalProvider.');
    }
    const {
      onConfirm,
      confirmationButtonText
    } = modalConfirm;
    return setConfirmationModalRequest({
      ...modalConfirm,
      confirmationButtonText: resolveConfirmationButtonText(confirmationButtonText, callbackParams),
      onConfirm: async () => {
        try {
          return await onConfirm(...callbackParams);
        } catch (error) {
          onError && (await onError(error));
          throw error;
        } finally {
          onFinally && (await onFinally());
        }
      }
    });
  }, [modalConfirm, onError, onFinally, setConfirmationModalRequest]);
}